import React from 'react';
import { Bot, Loader2 } from 'lucide-react';
import type { CopilotInitializeResponse } from '@/types/copilot';

interface MCPCopilotEmptyStateProps {
  show: boolean;
  isInitializing: boolean;
  initError: string | null;
  copilotProfile: CopilotInitializeResponse | null;
}

export const MCPCopilotEmptyState: React.FC<MCPCopilotEmptyStateProps> = ({
  show,
  isInitializing,
  initError,
  copilotProfile,
}) => {
  if (!show) {
    return null;
  }

  if (isInitializing) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin mb-3" />
        <p className="text-sm text-gray-600">Initializing copilot...</p>
      </div>
    );
  }

  if (initError) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex items-start gap-3">
          <div className="text-red-500 text-xl">⚠️</div>
          <div>
            <h3 className="font-semibold text-red-900 mb-1">Failed to Initialize Copilot</h3>
            <p className="text-sm text-red-700">{initError}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-3 text-sm text-red-600 hover:text-red-800 underline"
            >
              Try reloading the page
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!copilotProfile) {
    return (
      <div className="p-6 bg-white border border-dashed border-gray-200 rounded-lg text-center text-sm text-gray-600">
        Copilot profile information is not available right now.
      </div>
    );
  }

  return (
    <div className="p-6 bg-gradient-to-br from-primary/5 to-primary/10 rounded-lg border border-primary/20">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
          <Bot className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            Hello! I'm {copilotProfile.copilot_profile.persona_name}
          </h3>
          <p className="text-sm text-gray-600">
            I can help you with various tasks using {copilotProfile.tool_count} available tools.
          </p>
        </div>
      </div>
    </div>
  );
};
